/**
 * Settings domain API — user profile/settings, privacy (DSGVO), usage stats, system health
 * (Spec 059, E2). Moved bitgleich from the former monolithic `api.ts` — no behavior change (E5).
 */
import type { ApiCore } from './core';
import type { UserSettings, UserProfile } from '../settings';

export type { UserSettings, UserProfile } from '../settings';
export type UpdateUserSettingsRequest = Partial<UserSettings>;

export interface HealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  version?: string;
}

export interface VersionResponse {
  version: string;
  buildDate: string;
  gitCommit?: string;
  environment?: 'development' | 'staging' | 'production';
}

export interface IngestEmailAddressResponse {
  enabled: boolean;
  emailAddress: string | null;
}

export interface PrivacyPolicyResponse {
  version: string;
  lastUpdated: string;
  contentUrl: string;
  controller: { name: string; address: string; email: string };
}

export interface DeleteUserDataResult {
  documentsDeleted: number;
  feedbackDeleted: number;
  householdsDeleted: number;
  userProfileDeleted: boolean;
}

// === Usage Stats Types (Issue #102) ===

export interface TimeSeriesPoint {
  date: string;
  value: number;
}

export interface HouseholdDocumentCount {
  householdId: string;
  count: number;
}

export interface PersonDocumentCount {
  personName: string;
  count: number;
}

export interface DocumentStats {
  totalCount: number;
  countTrend: TimeSeriesPoint[];
  perHousehold: HouseholdDocumentCount[];
  perPerson: PersonDocumentCount[];
  avgFileSizeBytes: number;
  avgFileSizeTrend: TimeSeriesPoint[];
}

export interface AiUsageStats {
  totalCalls: number;
  callsTrend: TimeSeriesPoint[];
  avgCallsPerDocument: number;
  avgTokensPerDocument: number;
  tokensTrend: TimeSeriesPoint[];
  totalInputTokens: number;
  totalOutputTokens: number;
  inputTokensTrend: TimeSeriesPoint[];
  outputTokensTrend: TimeSeriesPoint[];
}

export interface PerformanceStats {
  avgProcessingDurationSeconds: number;
  durationTrend: TimeSeriesPoint[];
}

export interface UsageStatsResponse {
  documents: DocumentStats;
  aiUsage: AiUsageStats;
  performance: PerformanceStats;
}

export class SettingsApi {
  constructor(private readonly core: ApiCore) {}

  /**
   * Health check
   */
  async getHealth(): Promise<HealthResponse> {
    const response = await fetch(`${this.core.baseUrl}/health`);
    if (!response.ok) {
      throw new Error('Health check failed');
    }
    return response.json();
  }

  /**
   * Version information
   */
  async getVersion(): Promise<VersionResponse> {
    const response = await fetch(`${this.core.baseUrl}/api/v1/version`);
    if (!response.ok) {
      throw new Error('Version check failed');
    }
    return response.json();
  }

  /**
   * Get current user's profile
   */
  async getUserProfile(): Promise<UserProfile> {
    return this.core.fetchJson<UserProfile>(`${this.core.baseUrl}/api/v1/user/profile`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Get current user's settings
   */
  async getUserSettings(): Promise<UserSettings> {
    return this.core.fetchJson<UserSettings>(`${this.core.baseUrl}/api/v1/user/settings`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Update current user's settings
   */
  async updateUserSettings(settings: Partial<UpdateUserSettingsRequest>): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/user/settings`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify(settings),
    });
  }

  /** Public (anonymous) privacy policy metadata incl. the data controller (Spec 151 E1a). */
  async getPrivacyPolicy(): Promise<PrivacyPolicyResponse> {
    return this.core.fetchJson<PrivacyPolicyResponse>(
      `${this.core.baseUrl}/api/v1/privacy/policy`,
      undefined,
      true,
      true,
    );
  }

  /**
   * Get the configured email ingest address (ADR-034)
   */

  async getIngestEmailAddress(): Promise<IngestEmailAddressResponse> {
    return this.core.fetchJson<IngestEmailAddressResponse>(
      `${this.core.baseUrl}/api/v1/ingest/email-address`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  // === Data Privacy API (DSGVO Art. 17 + Art. 20) ===

  /**
   * Export all user data as a ZIP archive (DSGVO Art. 20 - Data Portability).
   * Returns a Blob for download.
   */
  async exportUserData(): Promise<Blob> {
    const response = await this.core.fetchWithErrorHandling(
      `${this.core.baseUrl}/api/v1/privacy/export`,
      { headers: this.core.getAuthHeaders() },
    );
    return response.blob();
  }

  /**
   * Delete all user data (DSGVO Art. 17 - Right to Erasure).
   */
  async deleteUserData(includeHouseholds: boolean = false): Promise<DeleteUserDataResult> {
    const response = await this.core.fetchWithErrorHandling(
      `${this.core.baseUrl}/api/v1/privacy/account?includeHouseholds=${includeHouseholds}`,
      {
        method: 'DELETE',
        headers: this.core.getAuthHeaders(),
      },
    );
    return response.json();
  }

  // === Usage Stats API ===

  /**
   * Get aggregated usage statistics (Issue #102)
   */
  async getUsageStats(
    timeRange: string = 'all',
    householdId?: string,
  ): Promise<UsageStatsResponse> {
    const params = new URLSearchParams({ timeRange });
    if (householdId) params.set('householdId', householdId);
    return this.core.fetchJson<UsageStatsResponse>(
      `${this.core.baseUrl}/api/v1/usage/stats?${params}`,
      { headers: this.core.getAuthHeaders() },
    );
  }
}
